import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Card,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import SelectDropdown from '../../../components/SelectDropdown';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '../../../components/Button';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import styles from './styles';
import OfferExpiryDate from './OfferExpiryDate';
import { Box } from '@mui/system';
import useDiscountOffer from '../hooks/useDiscountOffer';

const DiscountOffer = ({
  offersList = [],
  defaultExpiryDate,
  promotionData,
  setPromotionData,
  mode,
  isDiscountClickable,
  isAdmin,
  productsList,
  loadedPromotionData,
  isDateSelectionDisabled,
  fetchHeadlineData,
}) => {
  const {
    disableConfig,
    configureHeadline,
    headlineOfferValues,
    promoExpiry,
    showDatePickerForRegOfferID,
    offerApplicable,
    selectHeadline,
    customHeadline,
    customDisclaimer,
    isRequiredChecked,
    requiredValue,
    minRequiredTextFieldValue,
    excludedProducts,

    modifiedOfferData,

    setConfigureHeadline,
    handleOfferApplicable,
    handleHeadlineChange,
    handleCustomHeadline,
    handleCustomDisclaimer,
    handleDiscountIsRequired,
    handleToggleChange,
    handleMinRequiredTextField,
    handleExcludedProducts,
    handleAutocompleteChange,
    handleOfferExpiryDate,
    handleOfferExpiryDateForInstallation,
    handleScheduleExpDate,
    handlePurchaseExpDate,
    setShowDatePickerForRegOfferID,

    getOptionsForAutocomplete,
    renderModifiedHeadlineData,
  } = useDiscountOffer(
    defaultExpiryDate,
    promotionData,
    setPromotionData,
    mode,
    loadedPromotionData,
    offersList,
    fetchHeadlineData
  );

  // UI-specific rendering functions
  const renderTextPart = (part, index) => {
    return (
      <span key={`${part}_${index}`} style={{ ...styles.headingSmall, padding: '5px 1px 0 1px' }}>
        {part}
      </span>
    );
  };

  const renderAutocomplete = (part, index, templateVariables) => {
    const variableIndex = templateVariables.indexOf(part);
    const autocompleteValue = Object.keys(promotionData)?.length
      ? promotionData?.discount_offer?.custom_values?.X
      : headlineOfferValues[part]?.toString();

    return (
      <React.Fragment key={index}>
        &nbsp;
        {headlineOfferValues[part] !== null && (
          <Autocomplete
            sx={{
              '& .MuiOutlinedInput-input': {
                height: '20px',
              },
              '& .MuiAutocomplete-inputRoot': {
                paddingTop: 0,
                paddingBottom: 0,
              },
              margin: '2px 4px',
            }}
            disableClearable
            options={getOptionsForAutocomplete(variableIndex)}
            value={autocompleteValue}
            onInputChange={(event, newInputValue) => handleAutocompleteChange(newInputValue, part)}
            freeSolo
            renderInput={({ inputProps, params, ...rest }) => (
              <Box sx={{ position: 'relative', bottom: '10px' }}>
                <TextField
                  {...params}
                  type="number"
                  sx={{ ' & .MuiInputBase-root': { width: '100px' } }}
                  {...rest}
                  inputProps={{ ...inputProps, min: 0 }}
                  error={Number(inputProps.value) < 0}
                />
                {Number(inputProps.value) < 0 && (
                  <Typography
                    variant="caption"
                    color="error"
                    component="div"
                    sx={{ position: 'absolute' }}
                  >
                    Value must be non-negative
                  </Typography>
                )}
              </Box>
            )}
          />
        )}
        &nbsp;
      </React.Fragment>
    );
  };

  const renderModifiedHeadline = (offersList, headline) => {
    const { parts, templateVariables } = renderModifiedHeadlineData(offersList, headline);

    if (!templateVariables) {
      return null;
    }

    return parts.map((part, index) => {
      if (templateVariables.includes(part)) {
        return renderAutocomplete(part, index, templateVariables);
      } else {
        return renderTextPart(part, index);
      }
    });
  };

  return (
    <section
      style={
        mode === 'Edit' && promotionData?.status === 'ARCHIVED' && isAdmin === false
          ? { marginTop: '20px', ...styles.sectionSpacing, ...styles.disableCard }
          : styles.sectionSpacing
      }
    >
      <Typography style={styles.heading}>Discount Offer</Typography>
      {(isDiscountClickable !== undefined && !isDiscountClickable) ||
      promotionData?.finance_offer?.length === 2 ? (
        <div style={{ position: 'relative', width: 'fit-content' }}>
          <Tooltip
            arrow
            placement="top"
            leaveDelay={500}
            sx={styles.freezeDateTooltip}
            title={
              promotionData?.finance_offer?.length === 2
                ? 'Please remove one finance offer to include this offer'
                : 'Please select a branch to include this offer'
            }
          >
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={false} />}
                label="Include"
                sx={{ width: 'fit-content' }}
              />
            </FormGroup>
          </Tooltip>
        </div>
      ) : (
        <FormGroup sx={{ width: '100%' }}>
          <FormControlLabel
            control={<Checkbox checked={offerApplicable} onChange={handleOfferApplicable} />}
            label="Include"
            sx={{ width: 'fit-content' }}
          />
        </FormGroup>
      )}
      <Card
        sx={
          !offerApplicable
            ? { marginTop: '20px', ...styles.cardBox, ...styles.disableCard }
            : { marginTop: '20px', ...styles.cardBox }
        }
      >
        <div className="headline-dropdown" style={styles.divContainer}>
          <SelectDropdown
            label="Headline"
            options={modifiedOfferData || []}
            singleSelectDefaultValues=""
            handleFilterChange={handleHeadlineChange}
            updateSingleDropdownValue={selectHeadline}
            width={'-webkit-fill-available'}
            containerWidth={'65%'}
          />
          <Button
            sx={{ ...styles.buttonStyle, background: '#e0e0e0' }}
            disabled={disableConfig}
            startIcon={<EditIcon sx={{ paddingLeft: '5px' }} />}
            onClick={() => setConfigureHeadline(!configureHeadline)}
          >
            Configure
          </Button>
        </div>
        {configureHeadline && (
          <div>
            {selectHeadline === 'custom_offer' ? (
              <div className="customHeadline" style={{ padding: '10px 20px' }}>
                <div>
                  <p style={{ ...styles.headingSmall, paddingTop: '5px' }}>
                    Headline: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customHeadline}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text"
                    value={customHeadline}
                    onChange={(event) => {
                      handleCustomHeadline(event.target.value);
                    }}
                    fullWidth
                  />
                </div>
                <div>
                  <p style={{ ...styles.headingSmall, paddingTop: '15px' }}>
                    Disclaimer: &nbsp;{' '}
                    <span style={{ background: 'rgb(255, 228, 51)', wordWrap: 'break-word' }}>
                      {customDisclaimer}
                    </span>
                  </p>
                  <TextField
                    variant="standard"
                    multiline
                    label="Type your own text (optional)"
                    value={customDisclaimer}
                    onChange={(event) => {
                      handleCustomDisclaimer(event.target.value);
                    }}
                    fullWidth
                  />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', padding: '10px 20px', flexWrap: 'wrap' }}>
                <span style={{ ...styles.headingSmall, paddingTop: '5px' }}>Headline: &nbsp; </span>
                {renderModifiedHeadline(offersList, selectHeadline)}
              </div>
            )}
          </div>
        )}
        <div className="minimum-purchase-req" style={{ padding: '10px 20px' }}>
          <div className="title" style={{ display: 'flex', width: 'fit-content' }}>
            <Typography sx={styles.headingSmall}>Minimum purchase required</Typography>
            <Tooltip
              arrow
              placement="top"
              leaveDelay={500}
              sx={styles.minPurchaseTooltip}
              title="Enter a valid number below. If you don't need a minimum purchase, un-check the check box below."
            >
              <HelpIcon />
            </Tooltip>
          </div>
          <FormGroup sx={{ width: 'fit-content' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRequiredChecked}
                  onChange={handleDiscountIsRequired}
                  sx={{ width: 'fit-content' }}
                />
              }
              sx={{ whiteSpace: 'nowrap' }}
              label="Is required"
            />
          </FormGroup>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            {!requiredValue ? (
              <Tooltip
                arrow
                placement="top"
                leaveDelay={500}
                title="Please select an option to input a minimum purchase value"
              >
                <ToggleButtonGroup
                  value={requiredValue}
                  exclusive
                  onChange={handleToggleChange}
                  aria-label="units or quantity"
                  sx={{ padding: '20px 20px 10px 0', height: '30px' }}
                  disabled={!isRequiredChecked}
                >
                  <ToggleButton value="UNITS">UNITS</ToggleButton>
                  <ToggleButton value="DOLLAR_AMOUNT">$</ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            ) : (
              <ToggleButtonGroup
                value={requiredValue}
                exclusive
                onChange={handleToggleChange}
                aria-label="units or quantity"
                sx={{ padding: '20px 20px 10px 0', height: '30px' }}
                disabled={!isRequiredChecked}
              >
                <ToggleButton value="UNITS">UNITS</ToggleButton>
                <ToggleButton value="DOLLAR_AMOUNT">$</ToggleButton>
              </ToggleButtonGroup>
            )}
            <Box sx={{ position: 'relative' }}>
              <TextField
                variant="standard"
                fullWidth
                value={minRequiredTextFieldValue}
                required={isRequiredChecked}
                onChange={handleMinRequiredTextField}
                disabled={!requiredValue || !isRequiredChecked}
                type="number"
                inputProps={{ min: 0 }}
                error={minRequiredTextFieldValue < 0 && isRequiredChecked}
              />
              {minRequiredTextFieldValue < 0 && isRequiredChecked && (
                <Typography
                  variant="caption"
                  color="error"
                  component="div"
                  sx={{ position: 'absolute' }}
                >
                  Value must be non-negative
                </Typography>
              )}
            </Box>
          </div>
          <div className="exluded-products">
            <Typography sx={styles.headingSmall}>Products that are excluded</Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FormGroup row>
                {productsList?.map((eachProduct, i) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={excludedProducts.includes(eachProduct)}
                        id={eachProduct}
                        onChange={handleExcludedProducts}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    }
                    key={i}
                    label={eachProduct}
                  />
                ))}
              </FormGroup>
            </div>
          </div>
        </div>
        <OfferExpiryDate
          showDatePickerForRegOfferID={showDatePickerForRegOfferID}
          setShowDatePickerForRegOfferID={setShowDatePickerForRegOfferID}
          handleOfferExpiryDate={handleOfferExpiryDate}
          handleOfferExpiryDateForInstallation={handleOfferExpiryDateForInstallation}
          handleScheduleExpDate={handleScheduleExpDate}
          handlePurchaseExpDate={handlePurchaseExpDate}
          promoExpiry={promoExpiry}
          offerApplicable={offerApplicable}
          defaultExpiryDate={defaultExpiryDate}
          promotionData={promotionData}
          isDateSelectionDisabled={isDateSelectionDisabled}
          disableConfig={disableConfig}
        />
      </Card>
    </section>
  );
};

DiscountOffer.propTypes = {
  // Basic props
  offersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      template: PropTypes.string.isRequired,
      template_variables: PropTypes.arrayOf(PropTypes.string),
      nested_values: PropTypes.object,
    })
  ),
  defaultExpiryDate: PropTypes.instanceOf(Date),
  promotionData: PropTypes.shape({
    status: PropTypes.string,
    store_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    store_ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    discount_offer: PropTypes.shape({
      headline: PropTypes.string,
      headline_builder_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      disclaimer: PropTypes.string,
      custom: PropTypes.bool,
      minimum_purchase: PropTypes.shape({
        required: PropTypes.bool,
        type: PropTypes.oneOf(['UNITS', 'DOLLAR_AMOUNT']),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
      excluded_products: PropTypes.arrayOf(PropTypes.string),
      custom_values: PropTypes.object,
      offer_expiry: PropTypes.shape({
        expiry_dates: PropTypes.arrayOf(PropTypes.string),
        offer_expiry_type: PropTypes.string,
      }),
    }),
    finance_offer: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  }),
  setPromotionData: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['Create', 'Edit']),
  isDiscountClickable: PropTypes.bool,
  isAdmin: PropTypes.bool,
  productsList: PropTypes.arrayOf(PropTypes.string),
  loadedPromotionData: PropTypes.bool,
  isDateSelectionDisabled: PropTypes.bool,
  fetchHeadlineData: PropTypes.func.isRequired,
};

export default DiscountOffer;
